import React, { useEffect, useState, useRef } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast, Toaster } from "sonner";
import {
  Inbox as InboxIcon, MessageCircle, Send, Loader2, Sparkles, ShieldCheck,
  ChevronLeft, CheckCircle2, Clock, AlertCircle,
} from "lucide-react";
import { InboxAttachmentPicker, InboxAttachmentImage } from "@/components/InboxAttachment";
import AdminInbox from "@/pages/admin/AdminInbox";

function StatusBadge({ status }) {
  const map = {
    open: { bg: "bg-[#FBBF24]/12 border-[#FBBF24]/40 text-[#FBBF24]", label: "Open", Icon: AlertCircle },
    in_progress: { bg: "bg-[#1E3A8A]/25 border-[#3B82F6]/40 text-[#93C5FD]", label: "In Progress", Icon: Clock },
    resolved: { bg: "bg-emerald-500/12 border-emerald-400/40 text-emerald-300", label: "Resolved", Icon: CheckCircle2 },
  };
  const cfg = map[status] || map.open;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border ${cfg.bg}`}>
      <cfg.Icon size={11} /> {cfg.label}
    </span>
  );
}

function Composer({ onCreated }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachment, setAttachment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/inbox", { subject: subject.trim(), body: body.trim(), attachment });
      toast.success("Message sent to admin.");
      setSubject(""); setBody(""); setAttachment("");
      onCreated?.(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="glass-card gold-border p-5 space-y-3" data-testid="inbox-composer">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">
        <Sparkles size={12} /> New Message to Admin
      </div>
      <div>
        <label className="zfd-label">Subject</label>
        <input className="zfd-input" required minLength={3} maxLength={140}
          value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder="Brief summary of your concern" data-testid="inbox-subject" />
      </div>
      <div>
        <label className="zfd-label">Details ({body.length}/4000)</label>
        <textarea className="zfd-input min-h-[120px]" required minLength={3} maxLength={4000}
          value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Describe the issue, question, or complaint in as much detail as you can. Include reference numbers, screenshots (attach below), or timestamps if relevant."
          data-testid="inbox-body" />
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-white/10 flex-wrap gap-3">
        <InboxAttachmentPicker
          value={attachment}
          onChange={setAttachment}
          onError={(m) => toast.error(m)}
          disabled={busy}
        />
        <div className="flex items-center gap-3">
          <div className="text-[11px] text-white/50 hidden sm:flex items-center gap-1"><ShieldCheck size={12} /> Delivered directly to the admin.</div>
          <button type="submit" disabled={busy || subject.length < 3 || body.length < 3}
            className="btn-deploy px-4 py-2 inline-flex items-center gap-2 disabled:opacity-40" data-testid="inbox-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
          </button>
        </div>
      </div>
    </form>
  );
}

function ThreadView({ threadId, onBack }) {
  const [thread, setThread] = useState(null);
  const [busy, setBusy] = useState(true);
  const [reply, setReply] = useState("");
  const [attachment, setAttachment] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/inbox/${threadId}`);
      setThread(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [threadId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim() && !attachment) return;
    setSending(true);
    try {
      await api.post(`/inbox/${threadId}/reply`, { body: reply.trim(), attachment });
      setReply(""); setAttachment("");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSending(false); }
  };

  if (busy || !thread) return <div className="text-white/50 flex items-center gap-2 p-6"><Loader2 size={14} className="animate-spin" /> Loading thread…</div>;

  const isClosed = thread.status === "resolved";
  return (
    <div className="glass-card p-5 space-y-4" data-testid={`inbox-thread-${threadId}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button onClick={onBack} className="text-xs text-white/50 hover:text-white inline-flex items-center gap-1" data-testid="inbox-thread-back">
            <ChevronLeft size={12} /> All threads
          </button>
          <div className="mt-1 font-display text-xl font-black truncate">{thread.subject}</div>
          <div className="mt-1 flex items-center gap-2">
            <StatusBadge status={thread.status} />
            {thread.assigned_name && (
              <span className="text-[10px] uppercase tracking-widest text-white/45">Assigned: <strong className="text-white/80">{thread.assigned_name}</strong></span>
            )}
          </div>
        </div>
      </div>
      <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1" data-testid="inbox-thread-messages">
        {thread.messages.map((m) => {
          const mine = m.from_role === "user";
          const isAI = m.from_role === "ai_assistant";
          const alignRight = mine;
          const bubbleClass = isAI
            ? "bg-gradient-to-br from-[#7C3AED]/20 to-[#3B82F6]/15 border-[#A78BFA]/50 text-white"
            : mine
              ? "bg-[#1E3A8A]/40 border-[#1E3A8A]/60 text-white"
              : "bg-[#FBBF24]/10 border-[#FBBF24]/30 text-white";
          const labelClass = isAI
            ? "text-[#C4B5FD]"
            : mine
              ? "text-[#93C5FD]"
              : "text-[#FBBF24]";
          const label = isAI
            ? (<span className="inline-flex items-center gap-1"><Sparkles size={10} /> Zen · AI Assistant</span>)
            : mine
              ? "You"
              : `${m.from_name || "Admin"} · ${m.from_role === "admin" ? "SUPER ADMIN" : m.from_role === "sub_admin" ? "SUPPORT" : ""}`;
          return (
            <div key={m.id} className={`flex ${alignRight ? "justify-end" : "justify-start"}`}
              data-testid={isAI ? "inbox-message-ai" : undefined}>
              <div className={`max-w-[75%] rounded-lg px-3 py-2 border ${bubbleClass}`}>
                <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${labelClass}`}>
                  {label}
                </div>
                {m.body && <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>}
                {m.has_attachment && m.attachment_url && (
                  <InboxAttachmentImage url={m.attachment_url} />
                )}
                <div className="text-[10px] text-white/40 mt-1 font-mono-num">
                  {isAI && <span className="mr-1 text-[#A78BFA]/70">auto-reply ·</span>}
                  {m.created_at?.slice(0, 16).replace("T", " ")}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {isClosed ? (
        <div className="text-center text-xs text-emerald-300 border border-emerald-400/30 bg-emerald-500/8 rounded p-3">
          This thread is <strong>resolved</strong>. If your issue is not fixed, please open a new thread.
        </div>
      ) : (
        <form onSubmit={send} className="border-t border-white/10 pt-3 space-y-2">
          <div className="flex items-end gap-2">
            <textarea className="zfd-input flex-1 min-h-[64px]" value={reply} onChange={(e) => setReply(e.target.value)}
              maxLength={4000} placeholder="Type your reply…" data-testid="inbox-thread-reply-input" />
            <button type="submit" disabled={sending || (!reply.trim() && !attachment)} className="btn-deploy px-4 py-2.5 inline-flex items-center gap-2 disabled:opacity-40" data-testid="inbox-thread-reply-submit">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
            </button>
          </div>
          <InboxAttachmentPicker
            value={attachment}
            onChange={setAttachment}
            onError={(m) => toast.error(m)}
            disabled={sending}
          />
        </form>
      )}
    </div>
  );
}

export default function Inbox() {
  const { user } = useAuth();
  const isAdminSide =
    user?.role === "admin" ||
    (user?.role === "sub_admin" && (user?.admin_permissions || []).includes("inbox"));

  // Admins & inbox-scoped sub-admins land here from the sidebar too — but they
  // want the members' inbound queue, not the user-facing composer. Delegate.
  if (isAdminSide) return <AdminInbox />;

  return <MemberInbox />;
}

function MemberInbox() {
  const { user: _user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inbox/mine");
      setThreads(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="text-white/50 flex items-center gap-2 p-6"><Loader2 size={16} className="animate-spin" /> Loading your messages…</div>
  );

  if (selected) {
    return (
      <div className="space-y-5" data-testid="inbox-root">
        <Toaster position="top-right" richColors />
        <ThreadView threadId={selected} onBack={() => { setSelected(null); load(); }} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="inbox-root">
      <Toaster position="top-right" richColors />
      <div>
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">
          <InboxIcon size={12} /> Message the Admin
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-1">Direct line to the floor.</h1>
        <p className="text-white/60 text-sm mt-1 max-w-2xl">
          Have a concern, complaint, or question that can&apos;t wait? Open a thread below. The admin (or an assigned support
          teammate) will reply as soon as possible — you&apos;ll see the response right here.
        </p>
      </div>

      <Composer onCreated={(t) => { setThreads([t, ...threads]); setSelected(t.id); }} />

      <div className="glass-card p-4" data-testid="inbox-thread-list">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold mb-2 flex items-center gap-1">
          <MessageCircle size={11} /> Your threads · {threads.length}
        </div>
        {threads.length === 0 ? (
          <div className="text-white/45 text-sm p-6 text-center">No messages yet. Send your first one above.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {threads.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelected(t.id)}
                className="w-full text-left py-3 px-2 hover:bg-white/[0.03] rounded flex items-start gap-3"
                data-testid={`inbox-thread-item-${t.id}`}
              >
                <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${t.unread_by_user ? "bg-[#DC2626]" : "bg-white/15"}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className={`text-sm truncate ${t.unread_by_user ? "font-bold text-white" : "text-white/85"}`}>{t.subject}</div>
                    <StatusBadge status={t.status} />
                  </div>
                  <div className="text-xs text-white/50 truncate mt-0.5">{t.last_message_preview}</div>
                  <div className="text-[10px] text-white/35 mt-0.5 font-mono-num">Updated {t.updated_at?.slice(0, 16).replace("T", " ")} · {t.message_count} messages</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
